import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";

import Table from "../../components/TablePro/Table/index";
import useMutation from "../../hooks/useMutation";
import { UPDATE_USER_MUTATION } from "../../constants/graphql";
import { MOBILE_WAREHOUSE_LOCATION, WAREHOUSE_LOCATION } from "../../constants/graphql/queries";
import useAuth from "../../hooks/useAuth";
import initialSettings from "../../constants/tablePro/warehouse";
import LocationNameLink from "../../components/TablePro/components/warehouse/LocationNameLink";
import EditLocationDetails from "../../components/TablePro/components/warehouse/EditLocationDetails";
import InputLocationName from "./InputLocationName";
import { clearLocationParams, LOC_ID_PARAM, LOC_TYPE_PARAM } from "./locationParams";
import { useNavigate } from "react-router";
import useQueryParams from "../../hooks/useQueryParams";

// Context to allow InputLocationName to use local state instead of URL params
const LocationDetailsContext = React.createContext(null);

// Input component that opens location details via local state (for modal mode)
function ModalInputLocationName({ entity, value }) {
  const openLocationDetails = useContext(LocationDetailsContext);
  useEffect(() => {
    if (!openLocationDetails) return;
    openLocationDetails(entity.id, entity.__typename);
  }, [entity.id, entity.__typename, openLocationDetails]);
  return <LocationNameLink value={value} />;
}

const fieldComponents = {
  rowNumber: {
    component: ({ rowIndex }) => rowIndex + 1,
    unEditable: true,
    disableOrder: true,
  },
  locationName: {
    component: LocationNameLink,
    input: InputLocationName,
  },
  occupied: {
    component: ({ value }) => (value ? "Occupied" : "Empty"),
  },
  dimensions: {
    component: ({ value }) => {
      if (!value?.[0]?.height) return "";
      return `${value[0].height}"`;
    },
  },
  baseLocation: {
    component: ({ value, entity }) => {
      if (!entity) return "";

      // For mobile locations, show the first warehouse location name
      const isMobileLocation =
        entity.__typename === "MobileWarehouseLocation" ||
        (entity.warehouseLocations && Array.isArray(entity.warehouseLocations));

      if (isMobileLocation && entity.warehouseLocations?.length > 0) {
        return entity.warehouseLocations[0].locationName;
      }

      return "";
    },
  },
};

const modalFieldComponents = {
  ...fieldComponents,
  locationName: {
    ...fieldComponents.locationName,
    input: ModalInputLocationName,
  },
};

export default function WarehouseTable({
  modal,
  list,
  pagination,
  bulkSelectionProps,
  selectedIds = [],
  getLocationSelectionKey,
}) {
  const { user, setUser } = useAuth();
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  // When rendered inside a modal, use local state instead of URL params
  // to avoid changing the URL (which closes the parent modal).
  const [modalLocId, setModalLocId] = useState(null);
  const [modalLocType, setModalLocType] = useState(null);

  const locId = modal ? modalLocId : queryParams.get(LOC_ID_PARAM);
  const locType = modal ? modalLocType : queryParams.get(LOC_TYPE_PARAM);
  const shouldFetchWarehouseLocation = !!locId && locType === "WarehouseLocation";
  const shouldFetchMobileLocation = !!locId && locType === "MobileWarehouseLocation";

  const { data: warehouseLocationData } = useQuery(WAREHOUSE_LOCATION, {
    variables: { id: locId },
    skip: !shouldFetchWarehouseLocation,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const { data: mobileWarehouseLocationData } = useQuery(MOBILE_WAREHOUSE_LOCATION, {
    variables: { id: locId },
    skip: !shouldFetchMobileLocation,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const handleCloseLocationDetails = useCallback(() => {
    if (modal) {
      setModalLocId(null);
      setModalLocType(null);
      return;
    }
    const search = clearLocationParams(queryParams).toString();
    navigate({ search: search ? `?${search}` : "" });
  }, [modal, navigate, queryParams]);

  const openLocationDetails = useCallback((id, type) => {
    setModalLocId(id);
    setModalLocType(type);
  }, []);

  const activeFieldComponents = modal ? modalFieldComponents : fieldComponents;

  const warehouseTableSettings = React.useMemo(
    () =>
      isEmpty(user.warehouseTableSettings) ||
      user?.warehouseTableSettings?.version !== initialSettings.version
        ? initialSettings
        : user.warehouseTableSettings,
    [user.warehouseTableSettings]
  );
  const fields = React.useMemo(() => {
    return warehouseTableSettings.fields.map(field => ({
      ...field,
      ...(activeFieldComponents[field.id] || {}),
    }));
  }, [warehouseTableSettings.fields, activeFieldComponents]);

  const fieldsProps = useMemo(
    () => ({
      BULK_CHECKBOX: bulkSelectionProps,
    }),
    [bulkSelectionProps]
  );
  const getRowBackgroundColor = useCallback(
    entity => {
      if (
        entity &&
        getLocationSelectionKey &&
        selectedIds.includes(getLocationSelectionKey(entity))
      ) {
        return "#E8F0FE";
      }
      return undefined;
    },
    [getLocationSelectionKey, selectedIds]
  );

  const changeWarehouseSettings = warehouseTableSettings => {
    setUser({ ...user, warehouseTableSettings });
    return updateUser({
      variables: { warehouseTableSettings },
      onSuccess: data => {
        setUser({ ...user, warehouseTableSettings });
      },
    });
  };

  const handlePageChange = (event, value) => {
    pagination.onPageChange(value);
  };

  const selectedEntity = useMemo(() => {
    if (!locId || !locType) return null;
    const entityFromList = list.find(e => e.id === locId && e.__typename === locType);
    if (entityFromList) return entityFromList;

    if (locType === "WarehouseLocation" && warehouseLocationData?.warehouseLocation) {
      return {
        ...warehouseLocationData.warehouseLocation,
        __typename: "WarehouseLocation",
      };
    }

    if (locType === "MobileWarehouseLocation" && mobileWarehouseLocationData?.mobileWarehouseLocation) {
      return {
        ...mobileWarehouseLocationData.mobileWarehouseLocation,
        __typename: "MobileWarehouseLocation",
      };
    }

    return null;
  }, [list, locId, locType, warehouseLocationData, mobileWarehouseLocationData]);

  const content = list.length === 0 ? (
    <Paper>
      <Typography p={3} align="center" color="textSecondary">
        Empty
      </Typography>
    </Paper>
  ) : (
    <Paper sx={{ p: 2 }}>
      <Table
        fullScreen={false}
        size={warehouseTableSettings.size}
        disabledScroll={false}
        totalCount={list.length}
        order={warehouseTableSettings.order}
        loading={false}
        loadingFields={false}
        fields={fields}
        fieldsProps={fieldsProps}
        entities={list}
        onLoadMore={() => {}}
        onChangeEntity={() => {}}
        onChangeFields={data =>
          changeWarehouseSettings({
            ...warehouseTableSettings,
            ...data,
          })
        }
        onFillDown={() => {}}
        onCopyPaste={() => {}}
        getRowBackgroundColor={getRowBackgroundColor}
        setHeight={height => height - 50}
      />
      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            size="medium"
            color="primary"
            count={Math.ceil(pagination.totalCount / pagination.limit)}
            page={pagination.page}
            onChange={handlePageChange}
          />
        </Box>
      )}
      {selectedEntity && (
        <EditLocationDetails
          entity={selectedEntity}
          value=""
          onClose={handleCloseLocationDetails}
          modal={modal}
        />
      )}
    </Paper>
  );

  if (!modal) return content;

  return (
    <LocationDetailsContext.Provider value={openLocationDetails}>
      {content}
    </LocationDetailsContext.Provider>
  );
}
